import React from 'react';
import { Link } from 'react-router-dom';
import Layout from '../components/layout/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { companyInfo, mockDocuments } from '../data/mock';
import {
  Target,
  Eye,
  Heart,
  CheckCircle2,
  Download,
  FileText,
  Users,
  Award,
  Building2,
  ArrowRight,
  ExternalLink
} from 'lucide-react';

const NosotrosPage = () => {
  const values = [
    { name: 'Integridad', description: 'Actuamos con honestidad y transparencia en cada interacción.' },
    { name: 'Compromiso', description: 'Dedicación total a nuestros afiliados y al sector inmobiliario.' },
    { name: 'Profesionalismo', description: 'Excelencia en cada servicio y capacitación que ofrecemos.' },
    { name: 'Solidaridad', description: 'Apoyo mutuo entre todos los miembros de nuestra comunidad.' },
    { name: 'Honestidad', description: 'Verdad y ética como pilares fundamentales de nuestra operación.' },
  ];

  const documents = mockDocuments.filter(doc => doc.category === 'institucional');

  return (
    <Layout>
      {/* Hero Section */}
      <section className="relative py-20 lg:py-28 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <p className="text-gray-500 uppercase tracking-wider text-sm font-medium mb-3">
              ¿Quiénes Somos?
            </p>
            <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
              La Unión de Profesionales Inmobiliarios de México A.C.
            </h1>
            <p className="text-xl text-gray-600 leading-relaxed">
              Somos la mejor plataforma en el mercado inmobiliario para la realización de negocios,
              ofreciendo proyectos de alto nivel y apoyo integral al profesional inmobiliario.
            </p>
          </div>
        </div>
      </section>

      {/* About Content */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1600880292203-757bb62b4baf?w=700&h=500&fit=crop"
                alt="Equipo UPIM"
                className="rounded-2xl shadow-xl w-full"
              />
              <div className="absolute -bottom-6 -right-6 bg-gray-900 text-white rounded-xl p-6 shadow-xl hidden md:block">
                <p className="text-4xl font-bold">2016</p>
                <p className="text-sm opacity-90">Año de fundación</p>
              </div>
            </div>
            
            <div>
              <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6">
                Nuestra Historia
              </h2>
              <p className="text-gray-600 mb-6 leading-relaxed">
                La Unión de Profesionales Inmobiliarios de México A.C. (UPIM) fue fundada en 
                <strong> 2016</strong> por un grupo de empresarios motivados por metas comunes:
              </p>
              <ul className="space-y-4 mb-8">
                <li className="flex items-start space-x-3">
                  <CheckCircle2 className="h-6 w-6 text-gray-700 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-600">Ordenar y consolidar la industria inmobiliaria en México</span>
                </li>
                <li className="flex items-start space-x-3">
                  <CheckCircle2 className="h-6 w-6 text-gray-700 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-600">Trabajar de manera cercana con las autoridades</span>
                </li>
                <li className="flex items-start space-x-3">
                  <CheckCircle2 className="h-6 w-6 text-gray-700 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-600">Unir a todos los profesionales inmobiliarios para un mejor futuro</span>
                </li>
                <li className="flex items-start space-x-3">
                  <CheckCircle2 className="h-6 w-6 text-gray-700 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-600">Buscar un mejor futuro para la comercialización inmobiliaria</span>
                </li>
              </ul>
              <p className="text-gray-600 leading-relaxed">
                Contamos con múltiples beneficios y especialistas que dirigen cada rubro dentro 
                de nuestro desempeño como profesionistas, con un equipo especializado en cada 
                dirección a tu entera disposición.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Mission, Vision, Values */}
      <section className="py-20 lg:py-28 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-gray-500 uppercase tracking-wider text-sm font-medium mb-3">
              Nuestros Pilares
            </p>
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              ¿Qué nos define?
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Mission */}
            <Card className="bg-white border-0 shadow-lg hover:shadow-xl transition-shadow">
              <CardContent className="p-8">
                <div className="h-14 w-14 rounded-2xl bg-gray-100 flex items-center justify-center mb-6">
                  <Target className="h-7 w-7 text-gray-700" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">Misión</h3>
                <p className="text-gray-600 leading-relaxed">
                  {companyInfo.mission}
                </p>
              </CardContent>
            </Card>

            {/* Vision */}
            <Card className="bg-white border-0 shadow-lg hover:shadow-xl transition-shadow">
              <CardContent className="p-8">
                <div className="h-14 w-14 rounded-2xl bg-gray-100 flex items-center justify-center mb-6">
                  <Eye className="h-7 w-7 text-gray-700" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">Visión</h3>
                <p className="text-gray-600 leading-relaxed">
                  {companyInfo.vision}
                </p>
              </CardContent>
            </Card>

            {/* Values */}
            <Card className="bg-white border-0 shadow-lg hover:shadow-xl transition-shadow md:col-span-2 lg:col-span-1">
              <CardContent className="p-8">
                <div className="h-14 w-14 rounded-2xl bg-gray-100 flex items-center justify-center mb-6">
                  <Heart className="h-7 w-7 text-gray-700" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">Valores</h3>
                <ul className="space-y-2">
                  {companyInfo.values.map((value, index) => (
                    <li key={index} className="flex items-center space-x-2">
                      <CheckCircle2 className="h-4 w-4 text-gray-700" />
                      <span className="text-gray-600">{value}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Values Detailed */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Nuestros Valores en Acción
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Estos valores guían cada decisión que tomamos y cada servicio que ofrecemos.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {values.map((value, index) => (
              <div 
                key={index}
                className="p-6 rounded-2xl bg-gray-50 hover:bg-emerald-50 transition-colors group"
              >
                <div className="h-10 w-10 rounded-full bg-gray-100 flex items-center justify-center mb-4 group-hover:bg-gray-900 transition-colors">
                  <CheckCircle2 className="h-5 w-5 text-gray-700 group-hover:text-white transition-colors" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{value.name}</h3>
                <p className="text-gray-600 text-sm">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Documents Section */}
      <section className="py-20 lg:py-28 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="text-gray-500 uppercase tracking-wider text-sm font-medium mb-3">
              Documentos Importantes
            </p>
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Información importante
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Conoce los documentos más importantes que hacen de UPIM ser 100% seguro y confiable para ti.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            <a
              href="https://upim.org/wp-content/uploads/2024/02/Directorio-UPIM-documento.pdf"
              target="_blank"
              rel="noopener noreferrer"
              className="p-6 bg-white rounded-xl shadow-sm hover:shadow-lg transition-all group text-center"
            >
              <div className="h-14 w-14 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-4 group-hover:bg-gray-900 transition-colors">
                <Users className="h-7 w-7 text-gray-700 group-hover:text-white transition-colors" />
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Directorio</h3>
              <p className="text-sm text-gray-500 mb-4">Conoce a nuestro equipo directivo</p>
              <span className="inline-flex items-center text-gray-700 text-sm font-medium">
                Ver documento <ExternalLink className="ml-1 h-4 w-4" />
              </span>
            </a>

            <a
              href="https://upim.org/wp-content/uploads/2023/10/CODIGO-HONOR-Y-JUSTICIA-UPIM.doc.pdf"
              target="_blank"
              rel="noopener noreferrer"
              className="p-6 bg-white rounded-xl shadow-sm hover:shadow-lg transition-all group text-center"
            >
              <div className="h-14 w-14 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-4 group-hover:bg-gray-900 transition-colors">
                <Award className="h-7 w-7 text-gray-700 group-hover:text-white transition-colors" />
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Código de Ética</h3>
              <p className="text-sm text-gray-500 mb-4">Nuestros principios y estándares</p>
              <span className="inline-flex items-center text-gray-700 text-sm font-medium">
                Ver documento <ExternalLink className="ml-1 h-4 w-4" />
              </span>
            </a>

            <Link
              to="/aviso-privacidad"
              className="p-6 bg-white rounded-xl shadow-sm hover:shadow-lg transition-all group text-center"
            >
              <div className="h-14 w-14 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-4 group-hover:bg-gray-900 transition-colors">
                <FileText className="h-7 w-7 text-gray-700 group-hover:text-white transition-colors" />
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Aviso de Privacidad</h3>
              <p className="text-sm text-gray-500 mb-4">Protección de tus datos</p>
              <span className="inline-flex items-center text-gray-700 text-sm font-medium">
                Ver documento <ArrowRight className="ml-1 h-4 w-4" />
              </span>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 lg:py-28 bg-gray-900 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl lg:text-4xl font-bold mb-6">
            ¡Ponte en contacto con nosotros!
          </h2>
          <p className="text-gray-400 text-lg mb-8 max-w-2xl mx-auto">
            ¿Tienes preguntas sobre UPIM? ¿Quieres saber más sobre cómo podemos ayudarte 
            en tu carrera inmobiliaria? Estamos aquí para ayudarte.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              size="lg" 
              className="bg-white text-gray-900 hover:bg-gray-100 px-8 h-12"
              asChild
            >
              <Link to="/contacto">
                Contactar ahora
                <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
            </Button>
            <Button 
              size="lg" 
              variant="outline" 
              className="border-gray-600 text-white hover:bg-gray-800 px-8 h-12"
              asChild
            >
              <Link to="/afiliacion">
                Afíliate a UPIM
              </Link>
            </Button>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default NosotrosPage;
